import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Iconify from '../Iconify';
import axios from 'axios';
import { base_url } from '../../backendurl/url';
import { Config } from '../../../utils/commonFuctions/Token';
import { RefreshToken } from '../../../utils/commonFuctions/RefreshToken';

const StyledSelect = styled(Select)(({ theme }) => ({
     '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',   // remove extra border
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '0px',
    backgroundColor: '#fff',
  },
  '& .MuiSelect-select': {
      padding: '10px 12px',   // reduced from 12px 14px
    fontSize: '14px',      // optional: smaller text
    minHeight: 'unset', 
  },
}));

const RoleItem = styled(MenuItem)(({ theme, isselected }) => ({
  backgroundColor: isselected === 'true' ? '#4F46E5' : 'transparent',
  color: isselected === 'true' ? '#fff' : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: isselected === 'true' ? '#4338CA' : theme.palette.action.hover,
  },
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const CreateRoleItem = styled(MenuItem)(({ theme }) => ({
  color: '#4F46E5',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  padding: '12px 16px',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  marginLeft: '8px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}));

// This component follows the TransForm component pattern
const TransRoleDropdown = ({ 
  name,
  label,
  value,
  onChange,
  isDisabled = false,
  errors = {},
  placeholder = "Select role",
  required = false,
  onRoleCreate,
  onRoleEdit,
  onRoleDelete,
  ...rest
}) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${base_url}/api/roles`, Config);
      if (response?.status === 200) {
        setRoles(response.data.results || response.data);
      }
    } catch (error) {
      // Sample data fallback
      setRoles([
        { id: 1, name: 'Admin' },
        { id: 2, name: 'Manager' },
        { id: 3, name: 'Supervisor' }
      ]);
      RefreshToken(error, setErrorMessage, setNotification);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleRoleChange = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue === 'create_new_role') {
      // Trigger role creation modal
      if (onRoleCreate) {
        onRoleCreate();
      }
    } else {
      // Update form data using TransForm's onChange pattern
      onChange(name, selectedValue);
    }
  };

  const handleEditRole = (event, role) => {
    event.stopPropagation();
    if (onRoleEdit) {
      onRoleEdit(role);
    }
  };

  const handleDeleteRole = (event, role) => {
    event.stopPropagation();
    if (onRoleDelete) {
      onRoleDelete(role);
    }
  };

  const hasError = errors?.status || false;
  //const errorMessage = errors?.message || '';

  return (
    <div>
          <InputLabel 
        id={`${name}-select-label`}
        sx={{ 
          color: hasError ? 'error.main' : 'text.secondary',
          fontSize: '14px',
          fontWeight: 600,
          marginBottom:"1px"
        }}
      >
        {label} {required &&  <span style={{ color: 'red' }}>*</span>}
      </InputLabel>
   
    <FormControl 
    //   fullWidth
      error={hasError}
      disabled={isDisabled}
      {...rest}
    >
      {/* <InputLabel 
        id={`${name}-select-label`}
        sx={{ 
          color: hasError ? 'error.main' : 'text.secondary',
          fontSize: '14px',
          fontWeight: 500
        }}
      >
        {label} {required && '*'}
      </InputLabel> */}
      
      <StyledSelect
        labelId={`${name}-select-label`}
        name={name}
        value={value || ''}
        onChange={handleRoleChange}
        // label={`${label} ${required ? '*' : ''}`}
        disabled={isDisabled || loading}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) return placeholder;
          const selectedRole = roles.find(role => role.id === selected || role.name === selected);
          return selectedRole?.name || selected;
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              borderRadius: '8px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            }
          }
        }}
      >
        {/* Default placeholder */}
        <MenuItem value="" disabled sx={{ display: 'none' }}>
          <Typography color="text.secondary">{placeholder}</Typography>
        </MenuItem>

        {/* Role items */}
        {roles.map((role) => (
          <RoleItem
            key={role.id}
            value={role.id}
            isselected={((value === role.id) || (value === role.name)).toString()}
          >
            <Typography sx={{ flex: 1, color:"#64748B" }}>
              {role.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <ActionButton
                size="small"
                onClick={(e) => handleEditRole(e, role)}
                sx={{ 
                  color: value === role.id ? '#fff' : 'text.secondary',
                  '&:hover': {
                    color: value === role.id ? '#fff' : 'primary.main'
                  }
                }}
              >
                <Iconify icon="eva:edit-2-outline" width={16} height={16} />
              </ActionButton>
              <ActionButton
                size="small"
                onClick={(e) => handleDeleteRole(e, role)}
                sx={{ 
                  color: value === role.id ? '#fff' : 'text.secondary',
                  '&:hover': {
                    color: value === role.id ? '#fff' : 'error.main'
                  }
                }}
              >
                <Iconify icon="eva:trash-2-outline" width={16} height={16} />
              </ActionButton>
            </Box>
          </RoleItem>
        ))}

        {/* Divider and Create New Role option */}
        {/* {roles.length > 0 && <Divider />}
        <CreateRoleItem value="create_new_role">
          <ListItemIcon sx={{ minWidth: 'auto', marginRight: 1 }}>
            <Iconify icon="eva:plus-outline" width={20} height={20} color="primary.main" />
          </ListItemIcon>
          <ListItemText 
            primary="Create a new role" 
            primaryTypographyProps={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'primary.main'
            }}
          />
        </CreateRoleItem> */}
        {roles.length > 0 && <Divider />}
<CreateRoleItem value="create_new_role">
  <Typography sx={{ flex: 1, color: '#3C50E0', fontWeight: 500, fontSize: '14px' }}>
    Create a new role
  </Typography>
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    <ActionButton
      size="small"
      sx={{ 
        color: '#3C50E0',
        '&:hover': { color: 'primary.dark' }
      }}
    >
      <Iconify icon="eva:plus-outline" width={18} height={18} />
    </ActionButton>
  </Box>
</CreateRoleItem>
      </StyledSelect>
      
      {hasError && errorMessage && (
        <FormHelperText error>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
     </div>
  );
};

export default TransRoleDropdown;